import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { HeroCMS } from '../components/HeroCMS';
import { MembershipCTACMS } from '../components/MembershipCTACMS';
import { SeriesShowcaseCMS } from '../components/SeriesShowcaseCMS';
import GiveawaySection from '../components/GiveawaySection';
import { ListenModal } from '../components/ListenModal';
import { Play, Users, Briefcase, MapPin, ArrowRight, Radio, ChevronLeft, ChevronRight } from 'lucide-react';
import { membershipBenefits } from '../mockData';  // Only keep this - membership benefits are static
import { API_URL } from '../utils/apiUrl';
import { formatSeries } from '../utils/formatSeries';
import { extractYouTubeId } from '../utils/extractYouTubeId';
import { SEOHead } from '../components/SEOHead';
import './Home.css';

// Cache keys for instant hero rendering
const HERO_CACHE_KEY = 'golfnuts_hero_episode_cache';
const EPISODES_CACHE_KEY = 'golfnuts_episodes_cache';

// Helper to get cached data for instant render
const getCachedHeroData = () => {
  try {
    const cached = sessionStorage.getItem(HERO_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const getCachedEpisodes = () => {
  try {
    const cached = sessionStorage.getItem(EPISODES_CACHE_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch {
    return [];
  }
};

export const HomeCMS = () => {
  // Initialize state with cached data for instant render
  const [pageData, setPageData] = useState(null);
  const [cmsEpisodes, setCmsEpisodes] = useState(() => getCachedEpisodes());
  const [cmsHosts, setCmsHosts] = useState([]);
  const [cmsGuests, setCmsGuests] = useState([]);
  const [featuredHeroEpisode, setFeaturedHeroEpisode] = useState(() => getCachedHeroData());
  const [siteSettings, setSiteSettings] = useState(null);
  const [activeGiveaway, setActiveGiveaway] = useState(null); // Active giveaway from CMS
  const [homepageLayout, setHomepageLayout] = useState(null); // Homepage layout configuration
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [guestSlideIndex, setGuestSlideIndex] = useState(0);
  const [isGuestSliderPaused, setIsGuestSliderPaused] = useState(false);
  const [guestsPerView, setGuestsPerView] = useState(3);
  const [isGuestSectionVisible, setIsGuestSectionVisible] = useState(false);
  const guestSectionRef = useRef(null);

  // Intersection Observer to detect when guest section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsGuestSectionVisible(entry.isIntersecting);
      },
      { threshold: 0.3 } // Trigger when 30% of section is visible
    );

    if (guestSectionRef.current) {
      observer.observe(guestSectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Handle responsive guests per view
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 540) {
        setGuestsPerView(1);
      } else if (width <= 781) {
        setGuestsPerView(2);
      } else {
        setGuestsPerView(3);
      }
    };
    
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch page data
      const pagesResponse = await axios.get(`${API_URL}/api/cms/pages`);
      const homePage = pagesResponse.data.find(p => p.slug === '/');
      
      let pageResponse = null;
      if (homePage) {
        pageResponse = await axios.get(`${API_URL}/api/cms/pages/by-id/${homePage.id}`);
        setPageData(pageResponse.data);
      }
      
      // Fetch episodes, guests, featured hero episode, active giveaway, homepage layout, and site settings from CMS
      const [episodesRes, guestsRes, featuredRes, settingsRes, giveawayRes, layoutRes] = await Promise.all([
        axios.get(`${API_URL}/api/cms/episodes`),
        axios.get(`${API_URL}/api/cms/guests`),
        axios.get(`${API_URL}/api/cms/episodes/featured/hero`),
        axios.get(`${API_URL}/api/cms/settings`),
        axios.get(`${API_URL}/api/cms/giveaways/active`).catch(() => ({ data: null })), // Graceful fallback if no active giveaway
        axios.get(`${API_URL}/api/cms/homepage-layout`).catch(() => ({ data: null })) // Graceful fallback for layout
      ]);
      
      // Update state
      setCmsEpisodes(episodesRes.data);
      setSiteSettings(settingsRes.data);
      setCmsGuests(guestsRes.data);
      setFeaturedHeroEpisode(featuredRes.data);
      setActiveGiveaway(giveawayRes.data); // Will be null if no active giveaway
      setHomepageLayout(layoutRes.data); // Homepage layout configuration
      
      // Cache hero data for instant render on next visit
      try {
        sessionStorage.setItem(HERO_CACHE_KEY, JSON.stringify(featuredRes.data));
        sessionStorage.setItem(EPISODES_CACHE_KEY, JSON.stringify(episodesRes.data));
      } catch (e) {
        // sessionStorage can throw QuotaExceededError or in private mode — log but don't fail
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.debug('[HomeCMS] hero cache write skipped:', e?.message);
        }
      }
      
      // Get hosts from CMS blocks
      if (pageResponse?.data) {
        const hostsSection = pageResponse.data.sectionsData.find(s => 
          s.identifier === 'HOSTS_SHOWCASE' || s.sectionType === 'hosts_showcase' || s.title === 'Meet Your Hosts'
        );
        
        if (hostsSection) {
          const hostBlocks = hostsSection.blocksData
            .filter(b => b.blockType === 'HOST_CARD')
            .sort((a, b) => (a.order || 0) - (b.order || 0)); // Sort by order field
          
          const hostsFromBlocks = hostBlocks.map(block => ({
            id: block.id,
            name: block.content.name,
            role: block.content.role,
            bio: block.content.bio,
            headshot: block.content.imageUrl,
            socialLinks: block.content.socialLinks || {},
            isActive: block.content.isActive !== false,
            featuredEpisodes: [],
            order: block.order
          }));
          
          setCmsHosts(hostsFromBlocks);
        }
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleListenClick = (e, episode) => {
    e.preventDefault();
    setSelectedEpisode(episode);
    setIsModalOpen(true);
  };

  // Helper function to extract YouTube ID - moved to shared utility
  // (See: /app/frontend/src/utils/extractYouTubeId.js)

  // Get recent episodes from CMS with proper sorting:
  // 1. Episodes with YouTube links first (sorted by release date desc)
  // 2. Episodes without YouTube links second (sorted by release date desc)
  // 3. Take top 3
  const getRecentEpisodes = () => {
    if (!cmsEpisodes || cmsEpisodes.length === 0) return [];
    
    // Separate episodes with and without valid YouTube links
    const withYouTube = [];
    const withoutYouTube = [];
    
    cmsEpisodes.forEach(episode => {
      const youtubeId = extractYouTubeId(episode.youtubeId);
      const hasValidYouTube = youtubeId && /^[a-zA-Z0-9_-]{11}$/.test(youtubeId);
      
      if (hasValidYouTube) {
        withYouTube.push(episode);
      } else {
        withoutYouTube.push(episode);
      }
    });
    
    // Sort both arrays by release date descending
    const sortByDateDesc = (a, b) => {
      const dateA = a.publishDate ? new Date(a.publishDate) : new Date(0);
      const dateB = b.publishDate ? new Date(b.publishDate) : new Date(0);
      return dateB - dateA;
    };
    
    withYouTube.sort(sortByDateDesc);
    withoutYouTube.sort(sortByDateDesc);
    
    // Combine: YouTube episodes first, then non-YouTube
    const sortedEpisodes = [...withYouTube, ...withoutYouTube];
    
    // Return top 3
    return sortedEpisodes.slice(0, 3);
  };
  
  const recentEpisodes = getRecentEpisodes();
  const activeHosts = cmsHosts.filter(h => h.isActive);
  
  // Get all featured guests for homepage slider - prioritize featured, then fill with others
  const getFeaturedGuests = () => {
    if (cmsGuests.length === 0) return [];
    const featured = cmsGuests.filter(g => g.isFeatured);
    const nonFeatured = cmsGuests.filter(g => !g.isFeatured);
    return [...featured, ...nonFeatured]; // Return all guests, not limited
  };
  const featuredGuests = getFeaturedGuests();
  
  // Guest slider configuration - uses responsive guestsPerView
  const GUESTS_PER_VIEW = guestsPerView;
  const totalGuestSlides = Math.max(0, featuredGuests.length - GUESTS_PER_VIEW + 1);
  const hasMultipleSlides = featuredGuests.length > GUESTS_PER_VIEW;
  
  // Reset slide index when guestsPerView changes to avoid out-of-bounds
  useEffect(() => {
    if (guestSlideIndex >= totalGuestSlides && totalGuestSlides > 0) {
      setGuestSlideIndex(totalGuestSlides - 1);
    }
  }, [guestsPerView, totalGuestSlides, guestSlideIndex]);
  
  // Get visible guests based on current slide index
  const getVisibleGuests = useCallback(() => {
    if (featuredGuests.length <= GUESTS_PER_VIEW) {
      return featuredGuests;
    }
    return featuredGuests.slice(guestSlideIndex, guestSlideIndex + GUESTS_PER_VIEW);
  }, [featuredGuests, guestSlideIndex]);
  
  // Auto-advance guest slider - only when section is visible
  useEffect(() => {
    if (!hasMultipleSlides || isGuestSliderPaused || !isGuestSectionVisible) return;
    
    const interval = setInterval(() => {
      setGuestSlideIndex(prev => {
        const nextIndex = prev + 1;
        return nextIndex >= totalGuestSlides ? 0 : nextIndex;
      });
    }, 4000); // Change every 4 seconds
    
    return () => clearInterval(interval);
  }, [hasMultipleSlides, isGuestSliderPaused, totalGuestSlides, isGuestSectionVisible]);
  
  // Guest slider navigation
  const goToPrevGuest = () => {
    setGuestSlideIndex(prev => prev === 0 ? totalGuestSlides - 1 : prev - 1);
  };
  
  const goToNextGuest = () => {
    setGuestSlideIndex(prev => prev >= totalGuestSlides - 1 ? 0 : prev + 1);
  };

  const episodesSection = (
    <>
      <div className="section-header section-header-centered">
        <h2 className="heading-1">Recent Episodes</h2>
        <Link to="/episodes" className="btn-accent">View All Episodes</Link>
      </div>
      
      <div className="episodes-grid">
        {recentEpisodes.map((episode) => {
          // Use the extractYouTubeId function defined above
          const youtubeId = extractYouTubeId(episode.youtubeId);
          const hasValidYouTube = youtubeId && /^[a-zA-Z0-9_-]{11}$/.test(youtubeId);
          
          // Use the CMS showComingSoon field as single source of truth
          // If showComingSoon is true (checked), show the Coming Soon badge
          // If showComingSoon is false (unchecked), don't show badge regardless of YouTube status
          const showComingSoon = episode.showComingSoon === true;
          
          return (
            <Link to={`/episodes/${episode.slug}`} key={episode.id} className="episode-card hover-lift">
              <div className="episode-thumbnail">
                {showComingSoon ? (
                  <>
                    <img src={episode.thumbnail} alt={episode.title} />
                    <div className="coming-soon-overlay">
                      <span className="coming-soon-badge">Coming Soon</span>
                    </div>
                  </>
                ) : hasValidYouTube ? (
                  <>
                    <img 
                      src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`} 
                      alt={episode.title}
                      onError={(e) => { 
                        if (episode.thumbnail) {
                          e.target.src = episode.thumbnail; 
                        }
                      }}
                    />
                    <div className="play-overlay">
                      <Play size={48} color="white" />
                    </div>
                  </>
                ) : (
                  <>
                    <img src={episode.thumbnail} alt={episode.title} />
                    <div className="play-overlay">
                      <Play size={48} color="white" />
                    </div>
                  </>
                )}
              </div>
              <div className="episode-info">
                <h3 className="heading-3">{episode.title}</h3>
                <p className="body-small episode-description">{episode.description}</p>
                <div className="episode-meta">
                  <p className="body-small episode-meta-info">
                    {showComingSoon ? 'Coming Soon' : episode.duration} • {new Date(episode.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                  <span className="episode-series-badge">{formatSeries(episode.series)}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </>
  );

  // Helper function to truncate text
  const truncateText = (text, maxLength = 120) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength).trim() + '...more';
  };

  const hostsSection = (
    <>
      <div className="section-header">
        <h2 className="heading-1">Meet Your Hosts</h2>
        <p className="body-large">The voices behind Golf Nuts</p>
      </div>
      
      <div className="hosts-grid">
        {activeHosts.map((host) => (
          <div key={host.id} className="host-card network-card">
            <div className="host-image-wrapper">
              <img src={host.headshot} alt={host.name} className="host-image" />
            </div>
            <div className="host-info">
              <h3 className="heading-2">{host.name}</h3>
              <p className="host-role">{host.role}</p>
              <p className="body-medium">{truncateText(host.bio)}</p>
              <Link to="/hosts" className="btn-secondary">Learn More</Link>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const guestsSection = (
    <>
      <div className="section-header section-header-centered">
        <h2 className="heading-1">Featured Guests</h2>
        <Link 
          to="/guests" 
          className="btn-accent"
        >
          All Guests
        </Link>
      </div>
      
      <div 
        ref={guestSectionRef}
        className="guests-slider-container"
        onMouseEnter={() => setIsGuestSliderPaused(true)}
        onMouseLeave={() => setIsGuestSliderPaused(false)}
      >
        {/* Navigation arrows - only show if more than 4 guests */}
        {hasMultipleSlides && (
          <>
            <button 
              className="guest-slider-nav guest-slider-prev"
              onClick={goToPrevGuest}
              aria-label="Previous guests"
            >
              <ChevronLeft size={24} />
            </button>
            <button 
              className="guest-slider-nav guest-slider-next"
              onClick={goToNextGuest}
              aria-label="Next guests"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
        
        <div className="guests-slider-track">
          <div 
            className="guests-grid guests-grid-slider"
            style={{
              // Slide by one card at a time. Card width depends on guestsPerView
              transform: hasMultipleSlides ? `translateX(-${guestSlideIndex * (100 / GUESTS_PER_VIEW)}%)` : 'none'
            }}
          >
            {featuredGuests.map((guest) => {
              // Find episodes where this guest is tagged
              const guestEpisodes = cmsEpisodes.filter(ep => ep.guests && ep.guests.includes(guest.id));
              const hasEpisodes = guestEpisodes.length > 0;
              
              return (
                <div 
                  key={guest.id} 
                  className="guest-card-wrapper"
                >
                  <Link to={`/guests/${guest.id}`} className="guest-card hover-lift">
                    <img src={guest.image} alt={guest.name} className="guest-image" />
                    <div className="guest-overlay">
                      <h3 className="heading-3">{guest.name}</h3>
                      <p className="guest-title">{guest.role}</p>
                      <p className="guest-company">{guest.company}</p>
                    </div>
                  </Link>
                  {hasEpisodes && (
                    <Link 
                      to={`/guests/${guest.id}`}
                      className="guest-episode-button"
                      title={`View ${guest.name}'s episodes`}
                    >
                      <Radio size={20} />
                      <span>View Episodes ({guestEpisodes.length})</span>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Slide indicators - only show if more than 3 guests */}
        {hasMultipleSlides && (
          <div className="guest-slider-dots">
            {Array.from({ length: totalGuestSlides }).map((_, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <button
                key={`guest-slider-dot-${idx}`}
                className={`guest-slider-dot ${idx === guestSlideIndex ? 'active' : ''}`}
                onClick={() => setGuestSlideIndex(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );

  // Find sections from CMS data
  const heroSection = pageData?.sectionsData?.find(s => s.sectionType === 'hero');
  const seriesSection = pageData?.sectionsData?.find(s => s.sectionType === 'series_showcase');
  const membershipCTASection = pageData?.sectionsData?.find(s => s.sectionType === 'membership_cta');

  // Helper function to render a section with optional background color
  const renderSectionWithStyle = (sectionId, content, backgroundColor) => {
    const style = backgroundColor ? { backgroundColor } : {};
    
    // Different sections have different wrapper structures
    // Return null for disabled sections or sections with no content
    if (!content) return null;
    
    return (
      <div key={sectionId} style={style}>
        {content}
      </div>
    );
  };

  // Map section IDs to their content
  const getSectionContent = (sectionId) => {
    switch (sectionId) {
      case 'hero':
        return (
          <HeroCMS 
            cmsContent={heroSection} 
            cmsEpisodes={cmsEpisodes} 
            featuredEpisode={featuredHeroEpisode} 
            siteSettings={siteSettings}
            isLoading={loading}
          />
        );
      case 'series_showcase':
        return <SeriesShowcaseCMS cmsContent={seriesSection} cmsEpisodes={cmsEpisodes} />;
      case 'current_giveaway':
        // GiveawaySection only renders if there's an active giveaway
        return <GiveawaySection giveaway={activeGiveaway} />;
      case 'recent_episodes':
        return (
          <section className="recent-episodes-section">
            <div className="container">
              {episodesSection}
            </div>
          </section>
        );
      case 'hosts':
        return (
          <section className="hosts-section">
            <div className="container">
              {hostsSection}
            </div>
          </section>
        );
      case 'featured_guests':
        return (
          <section className="guests-section">
            <div className="container">
              {guestsSection}
            </div>
          </section>
        );
      case 'membership_cta':
        return <MembershipCTACMS cmsContent={membershipCTASection} />;
      default:
        return null;
    }
  };

  // Get sections in the order defined by the layout
  const getOrderedSections = () => {
    // Safe default sections - ALWAYS use these if layout is missing/invalid
    const defaultSections = [
      { id: 'hero', enabled: true, backgroundColor: null },
      { id: 'series_showcase', enabled: true, backgroundColor: null },
      { id: 'current_giveaway', enabled: true, backgroundColor: null },
      { id: 'recent_episodes', enabled: true, backgroundColor: null },
      { id: 'hosts', enabled: true, backgroundColor: null },
      { id: 'featured_guests', enabled: true, backgroundColor: null },
      { id: 'membership_cta', enabled: true, backgroundColor: null },
    ];
    
    // If no layout or no sections array, use defaults
    if (!homepageLayout || !homepageLayout.sections || !Array.isArray(homepageLayout.sections) || homepageLayout.sections.length === 0) {
      return defaultSections;
    }
    
    // Filter to only enabled sections
    const enabledSections = homepageLayout.sections.filter(section => section && section.enabled !== false);
    
    // If all sections are disabled, return defaults to prevent empty page
    if (enabledSections.length === 0) {
      return defaultSections;
    }
    
    return enabledSections;
  };

  // Create a data key to force re-render when data loads
  const dataKey = `${cmsEpisodes.length}-${cmsHosts.length}-${cmsGuests.length}`;

  return (
    <div className="home-page" key={dataKey}>
      <SEOHead
        title="The Golf World's Group Chat"
        description="Golf Nuts — the golf world's group chat. Friends, stories, and everything we love about the game. Watch our podcast, meet the guests, and join the Clubhouse."
        path="/"
      />
      {/* Dynamically render sections based on CMS layout order */}
      {getOrderedSections().map(section => 
        renderSectionWithStyle(
          section.id,
          getSectionContent(section.id),
          section.backgroundColor
        )
      )}

      {/* Listen Modal */}
      <ListenModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        episode={selectedEpisode}
      />
    </div>
  );
};
